/**
 * Report rendering — architecture §6 step 5 (PERSIST).
 *
 * `renderReportMarkdown` produces the human-facing weekly report (in-app + email);
 * `toCoachReportRow` produces the exact `public.coach_reports` insert payload.
 */
import type { CoachInput } from '../types/coach.js';
import type {
  CoachReport,
  EvaluationResult,
  ExerciseInsight,
  FatigueFlag,
  PlanProposal,
  PrescribedChange,
  SafetyRail,
} from '../types/results.js';

/** Non-medical disclaimer always appended to the rendered report. */
export const COACH_DISCLAIMER =
  'This report is coaching, not medical advice. It reads your log and adjusts your program. It cannot diagnose anything — if a movement hurts, stop it and see a qualified clinician.';

export interface ReportRenderOptions {
  /** Overrides the report title. Defaults to "Weekly Coach Report". */
  title?: string;
  /** Localised display name for the greeting line. */
  displayName?: string;
  /** Rendered at the top when the LLM produced the prose. */
  report?: CoachReport;
  /** Include the per-exercise evidence table (default true). */
  includeExerciseTable?: boolean;
  /** Include the safety-rail audit section (default true). */
  includeSafetyRails?: boolean;
  /** Injected clock for the "week of" line; defaults to the packet-free value in the evaluation. */
  generatedAt?: string;
}

function pct(value: number): string {
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(1)}%`;
}

/** Trim trailing zeros so 82.5 prints as "82.5 kg" and 90 as "90 kg". */
function num(value: number): string {
  if (!Number.isFinite(value)) return '—';
  const fixed = value.toFixed(2);
  return fixed.replace(/\.?0+$/, '');
}

function kg(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return `${num(value)} kg`;
}

function verdictBanner(verdict: EvaluationResult['verdict']): string {
  switch (verdict) {
    case 'deload_recommended':
      return '🟠 **DELOAD WEEK RECOMMENDED**';
    case 'overreaching':
      return '🔴 **OVERREACHING — PULL BACK**';
    case 'progressing_fast':
      return '🟢 **AHEAD OF PLAN**';
    case 'progressing':
      return '🟢 **PROGRESSING**';
    case 'stalled':
      return '🟠 **STALLED — CHANGE THE STIMULUS**';
    case 'insufficient_data':
      return '⚪ **NOT ENOUGH DATA YET**';
    case 'on_track':
    default:
      return '🟢 **ON TRACK**';
  }
}

function trendArrow(trend: ExerciseInsight['trend']): string {
  switch (trend) {
    case 'improving':
      return '↑';
    case 'declining':
      return '↓';
    case 'flat':
      return '→';
    case 'insufficient_data':
    default:
      return '·';
  }
}

function changeDescription(change: PrescribedChange): string {
  const fields = new Map(change.fields.map((field) => [field.field, field]));
  const sets = fields.get('sets');
  const weight = fields.get('targetWeightKg');
  const repMax = fields.get('repMax');
  const rpe = fields.get('targetRpe');
  const bits: string[] = [];
  if (sets && sets.before !== sets.after) bits.push(`${sets.before === null ? '—' : num(sets.before)} → ${sets.after === null ? '—' : num(sets.after)} sets`);
  if (weight && weight.before !== weight.after) {
    bits.push(`${kg(weight.before)} → ${kg(weight.after)}`);
  }
  if (repMax && repMax.before !== repMax.after) {
    bits.push(`top reps ${repMax.before === null ? '—' : num(repMax.before)} → ${repMax.after === null ? '—' : num(repMax.after)}`);
  }
  if (rpe && rpe.before !== rpe.after) bits.push(`RPE ${rpe.before === null ? '—' : num(rpe.before)} → ${rpe.after === null ? '—' : num(rpe.after)}`);
  if (bits.length === 0) bits.push('no numeric change');
  return bits.join(', ');
}

function fatigueSection(flags: ReadonlyArray<FatigueFlag>, verdict: EvaluationResult['verdict']): string {
  if (flags.length === 0) {
    return `### Fatigue & recovery\n\nNo fatigue flags fired this block. Keep logging session RPE so the coach can see trouble early.`;
  }
  const lines = flags.map(
    (flag) => `- **${flag.code}** (${flag.severity}) — ${flag.message}`,
  );
  const action =
    verdict === 'deload_recommended'
      ? 'Three or more flags (or climbing RPE on a flat e1RM) means deload: volume ×0.6, load −10%, stop at RPE 7.'
      : verdict === 'overreaching'
        ? 'Two flags is the warning line: cut weekly volume by about 20% and keep RPE at 8 or below.'
        : 'One flag is a nudge, not a verdict: watch it next week.';
  return `### Fatigue & recovery\n\n${lines.join('\n')}\n\n${action}`;
}

function prescribedChangesTable(proposal: PlanProposal): string {
  if (proposal.changes.length === 0) {
    return proposal.needsMoreData
      ? '### Prescribed changes\n\nNo changes yet — the coach wants another week of logs before it touches the plan.'
      : '### Prescribed changes\n\nNo changes. Run the plan as written.';
  }
  const header =
    '| Exercise | Day | Action | Before → After | Rule | Rationale |\n| --- | --- | --- | --- | --- | --- |';
  const rows = proposal.changes.map(
    (change) =>
      `| ${change.exerciseName} | ${change.dayLabel} | \`${change.action}\` | ${changeDescription(change)} | \`${change.rule}\` | ${change.rationale} |`,
  );
  return `### Prescribed changes — week ${proposal.week}\n\n${header}\n${rows.join('\n')}`;
}

function exerciseTable(insights: ReadonlyArray<ExerciseInsight>): string {
  if (insights.length === 0) return '### Exercise breakdown\n\nNo per-exercise trend data in this window.';
  const header =
    '| Exercise | Trend | Best e1RM / top set | Change | Weeks at load | Coach call |\n| --- | --- | --- | --- | --- | --- |';
  const rows = insights.map(
    (insight) =>
      `| ${insight.exerciseName} | ${trendArrow(insight.trend)} ${insight.trend} | ${insight.bodyweight ? `${insight.bestE1rmKg.toFixed(0)} reps` : kg(insight.bestE1rmKg)} | ${pct(insight.e1rmChangePct)} | ${insight.weeksAtCurrentLoad} | ${insight.recommendation} — ${insight.reason} |`,
  );
  return `### Exercise breakdown\n\n${header}\n${rows.join('\n')}`;
}

function safetySection(rails: ReadonlyArray<SafetyRail>): string {
  if (rails.length === 0) return '';
  const lines = rails.map((rail) => `- \`${rail.code}\` — ${rail.explanation}`);
  return `### Safety rails hit\n\n${lines.join('\n')}`;
}

function checklistSection(checklist: ReadonlyArray<string>): string {
  if (checklist.length === 0) return '';
  return `### Next-week checklist\n\n${checklist.map((item) => `- [ ] ${item}`).join('\n')}`;
}

function prSection(prCount: number, report?: CoachReport): string {
  if (prCount <= 0 && !report) return '';
  const notes =
    report?.exerciseNotes && report.exerciseNotes.length > 0
      ? `\n\n${report.exerciseNotes.map((note) => `- **${note.exerciseName}** — ${note.note}`).join('\n')}`
      : '';
  if (prCount <= 0) return `### PR watch\n\nNo PRs this block — the work is still going in.${notes}`;
  return `### 🟠 ${prCount} PR${prCount === 1 ? '' : 's'} this block\n\nThat is the log proving the plan works. Same again next week.${notes}`;
}

/**
 * Render the complete weekly report as markdown.
 *
 * Sections: verdict banner, three headline metrics, PR celebration, LLM summary
 * (when supplied), per-exercise table, prescribed-changes table with rationale,
 * fatigue/deload section, safety rails, next-week checklist, disclaimer.
 */
export function renderReportMarkdown(
  input: { evaluation: EvaluationResult; proposal: PlanProposal; railsHit?: SafetyRail[] },
  opts: ReportRenderOptions = {},
): string {
  const { evaluation, proposal } = input;
  const rails = input.railsHit ?? [];
  const title = opts.title ?? 'Lift2Fit — Weekly Coach Report';
  const generatedAt = opts.generatedAt ?? '';
  const includeExerciseTable = opts.includeExerciseTable ?? true;
  const includeSafetyRails = opts.includeSafetyRails ?? true;

  const sections: string[] = [];
  sections.push(`# ${title}`);
  if (generatedAt) sections.push(`_Generated ${generatedAt}_`);
  if (opts.displayName) sections.push(`## ${opts.displayName}`);
  sections.push(`${verdictBanner(evaluation.verdict)}\n\n${evaluation.headline}`);

  sections.push(
    [
      '### Headline numbers',
      '',
      `| Sessions | Volume | Avg RPE |`,
      `| --- | --- | --- |`,
      `| ${evaluation.metrics.sessionsAnalyzed} over ${evaluation.metrics.weeksAnalyzed} weeks | ${evaluation.metrics.totalVolumeKg.toFixed(0)} kg (${pct(evaluation.metrics.volumeChangePct)} vs week 1) | ${evaluation.metrics.avgRpe.toFixed(2)} |`,
      '',
      `Compliance ${evaluation.metrics.compliancePct.toFixed(1)}% · acute:chronic workload ${evaluation.metrics.acuteChronicRatio.toFixed(2)} · ${evaluation.metrics.prCount} PR${evaluation.metrics.prCount === 1 ? '' : 's'}`,
    ].join('\n'),
  );

  const prs = prSection(evaluation.metrics.prCount, opts.report);
  if (prs) sections.push(prs);

  if (opts.report) {
    sections.push(`### Coach summary\n\n${opts.report.summaryMd}`);
  }

  if (includeExerciseTable) sections.push(exerciseTable(evaluation.exerciseInsights));
  sections.push(prescribedChangesTable(proposal));
  sections.push(fatigueSection(evaluation.fatigueFlags, evaluation.verdict));
  if (includeSafetyRails) {
    const safety = safetySection(rails);
    if (safety) sections.push(safety);
  }

  const checklist = opts.report?.nextWeekChecklist ?? [];
  const checklistSectionText = checklistSection(checklist);
  if (checklistSectionText) sections.push(checklistSectionText);
  else sections.push('### Next-week checklist\n\n- [ ] Log every working set at RPE 8 or below until the trend says otherwise.');

  if (evaluation.dataQuality.length > 0) {
    sections.push(`### Data notes\n\n${evaluation.dataQuality.map((note) => `- ${note}`).join('\n')}`);
  }

  sections.push(`---\n\n_${COACH_DISCLAIMER}_`);
  return `${sections.join('\n\n')}\n`;
}

/** Exact insert shape for `public.coach_reports`. */
export interface CoachReportRow {
  user_id: string;
  run_id: string;
  week_start: string;
  window_days: number;
  verdict: EvaluationResult['verdict'];
  headline: string;
  summary_md: string;
  metrics: EvaluationResult['metrics'];
  evidence: {
    exerciseInsights: EvaluationResult['exerciseInsights'];
    fatigueFlags: EvaluationResult['fatigueFlags'];
    dataQuality: string[];
    verdictReasons: string[];
  };
  prescribed_changes: PrescribedChange[];
  safety_rails_hit: string[];
  model: string | null;
  model_tier: string | null;
  applied_at: string | null;
}

export interface CoachReportRowInput {
  packet: Pick<CoachInput, 'userId' | 'windowDays' | 'generatedAt' | 'profile'>;
  evaluation: EvaluationResult;
  proposal: PlanProposal;
  railsHit: ReadonlyArray<SafetyRail>;
  report: CoachReport;
  runId: string;
  /** ISO date for `coach_reports.week_start` (Monday). Defaults to this week's Monday. */
  weekStart?: string;
  model?: string;
  modelTier?: string;
  appliedAt?: string | null;
}

/** Monday (ISO week start) of an ISO timestamp. */
export function weekStartOf(timestamp: string): string {
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return timestamp.slice(0, 10);
  const utc = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  const day = utc.getUTCDay();
  const delta = day === 0 ? -6 : 1 - day;
  utc.setUTCDate(utc.getUTCDate() + delta);
  return utc.toISOString().slice(0, 10);
}

/**
 * Build the `coach_reports` row. `coach_reports` is unique on
 * `(user_id, week_start)`, so the caller passes `week_start` through unchanged for
 * an idempotent upsert.
 */
export function toCoachReportRow(input: CoachReportRowInput): CoachReportRow {
  const { packet, evaluation, proposal, railsHit, report } = input;
  return {
    user_id: packet.userId,
    run_id: input.runId,
    week_start: input.weekStart ?? weekStartOf(packet.generatedAt),
    window_days: packet.windowDays,
    verdict: evaluation.verdict,
    headline: report.headline,
    summary_md: report.summaryMd,
    metrics: evaluation.metrics,
    evidence: {
      exerciseInsights: evaluation.exerciseInsights,
      fatigueFlags: evaluation.fatigueFlags,
      dataQuality: evaluation.dataQuality,
      verdictReasons: evaluation.verdictReasons,
    },
    prescribed_changes: proposal.changes,
    safety_rails_hit: railsHit.map((rail) => rail.code),
    model: input.model ?? null,
    model_tier: input.modelTier ?? packet.profile.tier ?? null,
    applied_at: input.appliedAt ?? null,
  };
}
